/**
 * Where to send someone once they finish signing in.
 *
 * Sign-in leaves the app entirely (an OAuth provider, or an email client for a
 * magic link) and comes back through `/auth/callback`, so the destination has
 * to survive a full page load. It travels two ways, and either one alone is
 * enough:
 *
 * - as a `redirect` query param on the callback URL, which is what carries it
 *   when the link is opened in a fresh tab (magic link);
 * - in sessionStorage, which is what carries it when a provider drops or
 *   rewrites the query it was handed.
 */

const REDIRECT_QUERY_KEY = "redirect";
const STORAGE_KEY = "setlist-post-login-redirect";

export const DEFAULT_POST_LOGIN_PATH = "/setlist";

/**
 * Only same-origin, absolute-from-root paths are ever followed. Anything else —
 * a full URL, a protocol-relative `//evil.com`, a backslash the browser
 * normalises into one — is discarded rather than repaired, so a crafted
 * `?redirect=` can never bounce a freshly signed-in user off-site.
 */
export function sanitizeRedirectPath(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
  return raw;
}

/** The current page, in the form the redirect param expects. */
export function currentPathAsRedirect(): string | null {
  if (typeof window === "undefined") return null;
  return sanitizeRedirectPath(
    `${window.location.pathname}${window.location.search}`,
  );
}

function withRedirectParam(base: string, redirectTo?: string | null): string {
  const target = sanitizeRedirectPath(redirectTo);
  if (!target) return base;
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${REDIRECT_QUERY_KEY}=${encodeURIComponent(target)}`;
}

/** `/login`, carrying the page to come back to. */
export function loginHref(redirectTo?: string | null): string {
  return withRedirectParam("/login", redirectTo);
}

/** The absolute URL handed to the auth provider as its callback. */
export function authCallbackUrl(redirectTo?: string | null): string {
  return withRedirectParam(
    `${window.location.origin}/auth/callback`,
    redirectTo,
  );
}

/** Stash the destination for the same-browser leg of the round trip. */
export function rememberPostLoginRedirect(redirectTo?: string | null): void {
  const target = sanitizeRedirectPath(redirectTo);
  try {
    if (target) sessionStorage.setItem(STORAGE_KEY, target);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / storage disabled: the query param still covers us.
  }
}

/**
 * The destination to land on after a completed sign-in, preferring what the
 * callback URL carried. Clears the stash either way — a redirect is spent once
 * it is used, and a stale one must not hijack a later sign-in.
 */
export function consumePostLoginRedirect(queryValue?: unknown): string | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stashed; fall through to the query param.
  }
  return sanitizeRedirectPath(queryValue) ?? sanitizeRedirectPath(stored);
}
